// src/pages/LoginPage.js
import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../contexts/OrderContext';
import PendingOrderHandler from '../utils/pendingOrderHandler';
import styles from '../styles/LoginPage.module.css';
import GoogleSignInButton from '../components/GoogleSignInButton'; // Import the Google button
import { Helmet } from 'react-helmet';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const { updateOrderDetails } = useOrder();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      console.log('Login Location State:', location.state);
      console.log('Pending Order in Location State:', location.state?.pendingOrder);

      await login(email, password);

      // Check for pending order in localStorage
      const tempUserId = localStorage.getItem('pendingOrderUserId');
      console.log('Temp User ID from localStorage:', tempUserId);

      if (tempUserId) {
        try {
          const pendingOrder = await PendingOrderHandler.getPendingOrder(tempUserId);
          console.log('Pending Order Found:', pendingOrder);
          if (pendingOrder) {
            Object.keys(pendingOrder).forEach(key => {
              if (key !== 'createdAt' && key !== 'id') {
                console.log(`Restoring ${key}:`, pendingOrder[key]);
                updateOrderDetails(key, pendingOrder[key]);
              }
            });
            localStorage.removeItem('pendingOrderUserId');
            console.log('Navigating to /checkout/complete due to pending order');
            navigate('/checkout/complete', { state: { fromLogin: true } });
            return;
          }
        } catch (error) {
          console.error('Error restoring pending order:', error);
        }
      }

      // Check if login was triggered from checkout intent
      const fromCheckout = location.state?.from === '/checkout/complete';
      const pendingOrderFlag = location.state?.pendingOrder;
      console.log('fromCheckout:', fromCheckout);
      console.log('pendingOrder (Login):', pendingOrderFlag);
      if (fromCheckout || pendingOrderFlag) {
        console.log('Navigating to /checkout/complete due to checkout intent');
        navigate('/checkout/complete', { state: { fromLogin: true } });
        return;
      }

      console.log('Navigating to /dashboard');
      navigate('/dashboard');
    } catch (err) {
      console.error('Login Error:', err);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // Determine the redirect path based on location state
  const getRedirectPath = () => {
    if (location.state?.from === '/checkout/complete' || location.state?.pendingOrder) {
      return '/checkout/complete';
    }
    return '/dashboard';
  };

  return (
    <div className={styles.container}>
      <Helmet>
      <title>Login to Your Account | MakeMeSongs</title>
      <meta name="description" content="Sign in to your MakeMeSongs account to manage your custom songs, download your music, and request revisions. Access all your personalized music in one place." />
      <meta name="keywords" content="custom song login, personalized music account, access custom songs, custom music signin" />
      <meta property="og:title" content="Login to Your Account | MakeMeSongs" />
      <meta property="og:description" content="Sign in to manage your custom songs, download your music, and request revisions." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://makemesongs.com/login" />
      <meta property="og:image" content="https://makemesongs.com/og-image.jpg" />
      <link rel="canonical" href="https://makemesongs.com/login" />
      <meta name="robots" content="noindex" /> {/* Don't index login pages */}
    </Helmet>
      <div className={styles.loginBox}>
        <h1 className={styles.heading}>Log In</h1>
        {error && <div className={styles.error}>{error}</div>}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formControl}>
            <label htmlFor="email" className={styles.formLabel}>Email</label>
            <input
              type="email"
              id="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className={styles.formControl}>
            <label htmlFor="password" className={styles.formLabel}>Password</label>
            <input
              type="password"
              id="password"
              className={styles.input}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? 'Loading...' : 'Log In'}
          </button>
        </form>

        {/* Add Google Sign-in Button */}
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: '15px 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
            <p style={{ margin: '0 10px', color: '#666' }}>or</p>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
          </div>
          <GoogleSignInButton redirectPath={getRedirectPath()} />
        </div>

        <div className={styles.links}>
          <p>
            Don't have an account?{' '}
            <Link to="/register" className={styles.link}>Sign Up</Link>
          </p>
          <p>
            <Link to="/forgot-password" className={styles.link}>Forgot Password?</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;